// hooks/useAIConversion.ts
import { useCallback } from "react"
import { AxiosResponse } from "axios"
import { useMutation } from "@tanstack/react-query"
import { useAuthedAxios } from "hooks/core/useAuthedAxios"
import {
  convertQuestionnaireWithAI,
  checkAIConversionHealth,
} from "api/AI/convertQuestionnaire"
import { AIConversionResponse } from "types/AIConversion.types"

interface AIHealthResponse {
  status: string
  timestamp: string
  documentationLoaded: boolean
  geminiConfigured: boolean
  apiConfigured: boolean
  version: string
}

// Define the return type interface for the useAIConversion hook
interface UseAIConversionReturn {
  convertQuestionnaire: (
    file: File,
    projectId: string,
    isAsync: boolean
  ) => Promise<AxiosResponse<AIConversionResponse | string>>
  isConvertingQuestionnaire: boolean

  checkHealth: () => Promise<AxiosResponse<AIHealthResponse>>
  isCheckingHealth: boolean
}

/**
 * AI questionnaire conversion — long-running commands run as react-query
 * mutations (isPending + devtools), no caching.
 */
export const useAIConversion = (): UseAIConversionReturn => {
  const getAxios = useAuthedAxios()

  const convertMutation = useMutation({
    mutationFn: async (args: [File, string, boolean]) =>
      convertQuestionnaireWithAI(await getAxios(), ...args),
  })

  const healthMutation = useMutation({
    mutationFn: async () => checkAIConversionHealth(await getAxios()),
  })

  const convertQuestionnaire = useCallback(
    (file: File, projectId: string, isAsync: boolean) =>
      convertMutation.mutateAsync([file, projectId, isAsync]),
    [convertMutation.mutateAsync]
  )

  return {
    convertQuestionnaire,
    isConvertingQuestionnaire: convertMutation.isPending,
    checkHealth: healthMutation.mutateAsync,
    isCheckingHealth: healthMutation.isPending,
  }
}
